import React, { useEffect, useRef } from "react";
import { Cpu, HardDrive, KeyRound, MemoryStick, Server } from "lucide-react";
import { useI18n } from "../i18n";

const ICONS = [Cpu, MemoryStick, HardDrive, Server, KeyRound];
const SPEED = 0.38;

function wrapOffset(index, phase, count) {
  let d = index - (phase % count);
  if (d > count / 2) d -= count;
  if (d < -count / 2) d += count;
  return d;
}

function cardStyle(offset) {
  const abs = Math.abs(offset);
  const y = offset * 118;
  const z = 90 - abs * 170;
  const rotateX = offset * -40;
  const zoom = Math.max(0, 1 - abs);
  const scale = 0.82 + zoom * 0.5;
  return {
    transform: `translate3d(108px, ${y}px, ${z}px) rotateX(${rotateX}deg) rotateY(8deg) scale(${scale})`,
    opacity: Math.max(0.35, 0.45 + zoom * 0.55),
    zIndex: Math.round(24 - abs * 8),
  };
}

export default function HeroMotion() {
  const { t } = useI18n();
  const steps = t.hero.flow || [];
  const count = steps.length;
  const stageRef = useRef(null);
  const cardRefs = useRef([]);
  const phaseRef = useRef(0);
  const holdRef = useRef(null);
  const reduceRef = useRef(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    reduceRef.current = reduce;
    const cards = cardRefs.current;

    const paint = (phase) => {
      cards.forEach((el, i) => {
        if (!el) return;
        const offset = wrapOffset(i, phase, count);
        const style = cardStyle(offset);
        el.style.transform = style.transform;
        el.style.opacity = String(style.opacity);
        el.style.zIndex = String(style.zIndex);
        el.classList.toggle("is-front", Math.abs(offset) < 0.5);
      });
    };

    paint(0);
    if (reduce || count < 2) return undefined;

    let frame = 0;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const hold = holdRef.current;
      if (hold != null) {
        let err = hold - (phaseRef.current % count);
        if (err > count / 2) err -= count;
        if (err < -count / 2) err += count;
        phaseRef.current += err * Math.min(1, dt * 7);
      } else {
        phaseRef.current += dt * SPEED;
      }
      paint(phaseRef.current);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [count]);

  const onMove = (event) => {
    const stage = stageRef.current;
    if (!stage || reduceRef.current) return;
    const rect = stage.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    stage.style.setProperty("--tilt-x", `${(-y * 9).toFixed(2)}deg`);
    stage.style.setProperty("--tilt-y", `${(x * 14).toFixed(2)}deg`);
  };

  const onLeave = () => {
    holdRef.current = null;
    const stage = stageRef.current;
    if (!stage) return;
    stage.style.setProperty("--tilt-x", "0deg");
    stage.style.setProperty("--tilt-y", "0deg");
  };

  return (
    <div className="nfw-hero-motion">
      <div
        className="nfw-hero-stage"
        ref={stageRef}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <div className="nfw-hero-stage-world" aria-hidden="true">
          <span className="nfw-hero-stage-ring" />
          <span className="nfw-hero-stage-ring nfw-hero-stage-ring-2" />
          <span className="nfw-hero-stage-beam" />
          <span className="nfw-hero-stage-floor" />
          {[0, 1, 2, 3, 4].map((i) => (
            <i className="nfw-hero-spark" key={i} />
          ))}
        </div>
        <div className="nfw-hero-reel">
          {steps.map((step, i) => {
            const Icon = ICONS[i] || Cpu;
            const style = cardStyle(wrapOffset(i, 0, count || 1));
            return (
              <a
                key={step.href}
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
                className={`nfw-hero-flow-card${i === 0 ? " is-front" : ""}`}
                href={step.href}
                style={style}
                onMouseEnter={() => {
                  holdRef.current = i;
                }}
                onFocus={() => {
                  holdRef.current = i;
                }}
                onBlur={() => {
                  holdRef.current = null;
                }}
              >
                <span className="nfw-hero-flow-num">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="nfw-hero-flow-icon">
                  <Icon size={22} />
                </span>
                <span className="nfw-hero-flow-copy">
                  <b>{step.tag}</b>
                  <strong>{step.title}</strong>
                  {step.note ? <em>{step.note}</em> : null}
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
